import React, { useState, useMemo, useEffect } from 'react';
import {
  Plus,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Search,
  DollarSign,
  User,
  Eye,
  Download,
  Printer,
  Check,
  Trash2,
  X,
  AlertCircle,
  AlertTriangle,
  Send,
  Mail,
  ExternalLink,
  RefreshCw
} from 'lucide-react';
import apiService from './services/api';
import { Button, LoadingSpinner } from './components';

// Helper to get ISO week number
const getISOWeek = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

// Get previous week info
const getPreviousWeekInfo = () => {
  const now = new Date();
  const currentWeek = getISOWeek(now);
  const currentYear = now.getFullYear();

  if (currentWeek === 1) {
    return { week: 52, year: currentYear - 1 };
  }
  return { week: currentWeek - 1, year: currentYear };
};

const prevWeekInfo = getPreviousWeekInfo();

function PayrollPage() {
  const [payrolls, setPayrolls] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [technicianFilter, setTechnicianFilter] = useState('all');
  const [weekFilter, setWeekFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('week');
  const [sortDirection, setSortDirection] = useState('desc');
  const [createPayroll, setCreatePayroll] = useState({
    technician: '',
    week: '',
    year: '2026'
  });
  const [creating, setCreating] = useState(false);
  const [creatingAll, setCreatingAll] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createAllResult, setCreateAllResult] = useState(null);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [recomputing, setRecomputing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [requestingPayment, setRequestingPayment] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [emailResult, setEmailResult] = useState(null);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [emailPreviewData, setEmailPreviewData] = useState(null);
  const [loadingEmailPreview, setLoadingEmailPreview] = useState(false);
  const [previewPayrollId, setPreviewPayrollId] = useState(null);
  const [showAdjustmentForm, setShowAdjustmentForm] = useState(false);
  const [adjustmentData, setAdjustmentData] = useState({ appointment_id: '', tip: '', service_amount: '', note: '' });
  const [adjustmentLoading, setAdjustmentLoading] = useState(false);
  const [adjustmentError, setAdjustmentError] = useState(null);
  const [adjustmentSuccess, setAdjustmentSuccess] = useState(null);
  const [appointmentPreview, setAppointmentPreview] = useState(null);
  const [lookingUpAppointment, setLookingUpAppointment] = useState(false);
  const itemsPerPage = 15;

  // Fetch payrolls and technicians on mount
  useEffect(() => {
    fetchData(true); // true = set default filter to most recent week
  }, []);

  const fetchData = async (setDefaultFilter = false) => {
    try {
      setLoading(true);
      setError(null);

      const [payrollsData, techniciansData] = await Promise.all([
        apiService.getPayrolls(),
        apiService.getStaff({ is_technician: true })
      ]);

      const payrollsList = payrollsData.results || payrollsData || [];
      setPayrolls(payrollsList);
      setTechnicians(techniciansData.results || techniciansData || []);

      // Set default filter to most recent week with data
      if (setDefaultFilter && payrollsList.length > 0) {
        // Find the most recent week/year combination
        const sorted = [...payrollsList].sort((a, b) => {
          if (a.year !== b.year) return b.year - a.year;
          return b.week - a.week;
        });
        const mostRecent = sorted[0];
        if (mostRecent) {
          setWeekFilter(mostRecent.week.toString());
          setYearFilter(mostRecent.year.toString());
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Get unique technician names from payroll data for filtering
  const technicianNames = useMemo(() => {
    if (!Array.isArray(payrolls)) return [];

    const uniqueNames = new Set();
    payrolls.forEach(payroll => {
      const name = `${payroll.contractor?.person?.first_name || ''} ${payroll.contractor?.person?.last_name || ''}`.trim();
      if (name) {
        uniqueNames.add(name);
      }
    });
    return Array.from(uniqueNames).sort();
  }, [payrolls]);

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    if (!Array.isArray(payrolls)) return [];

    let filtered = payrolls.filter(payroll => {
      const technicianName = `${payroll.contractor?.person?.first_name || ''} ${payroll.contractor?.person?.last_name || ''}`.trim();
      const matchesSearch =
        technicianName.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesTechnician = technicianFilter === 'all' || technicianName === technicianFilter;
      const matchesWeek = weekFilter === 'all' || payroll.week.toString() === weekFilter;
      const matchesYear = yearFilter === 'all' || payroll.year.toString() === yearFilter;
      const payrollStatus = payroll.confirmed ? 'paid' : (payroll.mercury_request_id ? 'processing_mercury' : 'pending');
      const matchesStatus = statusFilter === 'all' || payrollStatus === statusFilter;

      return matchesSearch && matchesTechnician && matchesWeek && matchesYear && matchesStatus;
    });

    // Sort data
    filtered.sort((a, b) => {
      let aValue, bValue;

      if (sortField === 'technician') {
        aValue = `${a.contractor?.person?.first_name || ''} ${a.contractor?.person?.last_name || ''}`.trim();
        bValue = `${b.contractor?.person?.first_name || ''} ${b.contractor?.person?.last_name || ''}`.trim();
      } else {
        aValue = a[sortField];
        bValue = b[sortField];
      }

      if (sortField === 'amount') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [payrolls, searchTerm, technicianFilter, weekFilter, yearFilter, statusFilter, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredAndSortedData.slice(startIndex, endIndex);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleCreatePayroll = async () => {
    if (!createPayroll.technician || !createPayroll.week || !createPayroll.year) {
      setCreateError('Please select team member, week, and year');
      return;
    }

    setCreating(true);
    setCreateError(null);

    try {
      await apiService.computePayroll(
        createPayroll.technician,
        createPayroll.week,
        createPayroll.year
      );

      // Refresh payrolls list
      await fetchData();

      // Reset form
      setCreatePayroll({
        technician: '',
        week: '',
        year: '2026'
      });
    } catch (err) {
      console.error('Error creating payroll:', err);
      setCreateError(err.message || 'Failed to create payroll');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateAllPayrolls = async () => {
    if (!createPayroll.week || !createPayroll.year) {
      setCreateError('Please select week and year to create all payrolls');
      return;
    }

    setCreatingAll(true);
    setCreateError(null);
    setCreateAllResult(null);

    try {
      const result = await apiService.computeAllPayroll(
        createPayroll.week,
        createPayroll.year
      );

      setCreateAllResult(result);

      // Refresh payrolls list
      await fetchData();

      // Update week/year filter to show the newly created payrolls
      setWeekFilter(createPayroll.week);
      setYearFilter(createPayroll.year);
    } catch (err) {
      console.error('Error creating all payrolls:', err);
      setCreateError(err.message || 'Failed to create payrolls');
    } finally {
      setCreatingAll(false);
    }
  };

  const handleViewDetails = async (payroll) => {
    try {
      const details = await apiService.getPayroll(payroll.id);
      setSelectedPayroll(details);
      setShowDetailModal(true);
    } catch (err) {
      console.error('Error fetching payroll details:', err);
    }
  };

  const handleConfirmPayroll = async (payrollId) => {
    setConfirming(payrollId);
    try {
      await apiService.confirmPayroll(payrollId);
      await fetchData();
    } catch (err) {
      console.error('Error confirming payroll:', err);
    } finally {
      setConfirming(null);
    }
  };

  const handleRecompute = async (payrollId) => {
    if (!window.confirm('Recompute this payroll with current appointment data (tips, services)?')) return;
    setRecomputing(payrollId);
    try {
      await apiService.post(`/amn/payrolls/${payrollId}/recompute/`);
      await fetchData();
    } catch (err) {
      alert('Recompute failed: ' + (err.message || 'Unknown error'));
    } finally {
      setRecomputing(null);
    }
  };

  const handleDeletePayroll = async (payrollId) => {
    if (!window.confirm('Are you sure you want to delete this payroll?')) return;

    setDeleting(payrollId);
    try {
      await apiService.deletePayroll(payrollId);
      await fetchData();
    } catch (err) {
      console.error('Error deleting payroll:', err);
    } finally {
      setDeleting(null);
    }
  };

  const handleRequestPayment = async (payroll) => {
    const techName = getTechnicianName(payroll);
    if (!window.confirm(`Request Mercury payment of $${payroll.amount} to ${techName}?\n\nThis will create a pending payment request that requires approval in Mercury.`)) {
      return;
    }

    setRequestingPayment(payroll.id);
    setPaymentResult(null);

    try {
      const result = await apiService.requestPayrollPayment(payroll.id);
      setPaymentResult({
        success: true,
        message: result.message || `Payment request created for ${techName}`,
        payrollId: payroll.id,
      });
    } catch (err) {
      console.error('Error requesting payment:', err);
      setPaymentResult({
        success: false,
        message: err.message || 'Failed to create payment request',
        payrollId: payroll.id,
      });
    } finally {
      setRequestingPayment(null);
    }
  };

  const getTechnicianName = (payroll) => {
    return `${payroll.contractor?.person?.first_name || ''} ${payroll.contractor?.person?.last_name || ''}`.trim();
  };

  const handleSendEmail = async (payroll) => {
    // Show preview first
    setLoadingEmailPreview(true);
    setPreviewPayrollId(payroll.id);
    setEmailResult(null);

    try {
      const preview = await apiService.previewPayrollEmail(payroll.id);
      setEmailPreviewData(preview);
      setShowEmailPreview(true);
    } catch (err) {
      console.error('Error loading email preview:', err);
      setEmailResult({
        success: false,
        message: err.message || 'Failed to load email preview',
        payrollId: payroll.id,
      });
    } finally {
      setLoadingEmailPreview(false);
    }
  };

  const handleConfirmSendEmail = async () => {
    if (!previewPayrollId) return;

    setSendingEmail(previewPayrollId);
    setShowEmailPreview(false);

    try {
      const result = await apiService.sendPayrollEmail(previewPayrollId);
      setEmailResult({
        success: true,
        message: result.message || `Email sent to ${emailPreviewData?.technician_name}`,
        payrollId: previewPayrollId,
      });
    } catch (err) {
      console.error('Error sending email:', err);
      setEmailResult({
        success: false,
        message: err.message || 'Failed to send email',
        payrollId: previewPayrollId,
      });
    } finally {
      setSendingEmail(null);
      setPreviewPayrollId(null);
      setEmailPreviewData(null);
    }
  };

  const handleLookupAppointment = async () => {
    const id = adjustmentData.appointment_id.trim();
    if (!id) return;
    setLookingUpAppointment(true);
    setAppointmentPreview(null);
    setAdjustmentError(null);
    try {
      const appt = await apiService.getAppointment(id);
      setAppointmentPreview(appt);
    } catch {
      setAppointmentPreview(null);
      setAdjustmentError('Appointment not found');
    } finally {
      setLookingUpAppointment(false);
    }
  };

  const handleAddAdjustment = async () => {
    if (!selectedPayroll) return;
    setAdjustmentLoading(true);
    setAdjustmentError(null);
    setAdjustmentSuccess(null);
    const tip = parseFloat(adjustmentData.tip) || 0;
    const serviceAmt = parseFloat(adjustmentData.service_amount) || 0;
    if (tip === 0 && serviceAmt === 0) {
      setAdjustmentError('Enter a tip or service amount');
      setAdjustmentLoading(false);
      return;
    }
    try {
      const payload = {
        tip,
        service_amount: serviceAmt,
        note: adjustmentData.note,
      };
      if (adjustmentData.appointment_id.trim()) {
        payload.appointment_id = parseInt(adjustmentData.appointment_id);
      }
      const updated = await apiService.addPayrollAdjustment(selectedPayroll.id, payload);
      setSelectedPayroll(updated);
      setAdjustmentSuccess('Adjustment added successfully');
      setAdjustmentData({ appointment_id: '', tip: '', service_amount: '', note: '' });
      setAppointmentPreview(null);
      setShowAdjustmentForm(false);
      fetchData(false);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to add adjustment';
      setAdjustmentError(msg);
    } finally {
      setAdjustmentLoading(false);
    }
  };

  const resetAdjustmentForm = () => {
    setShowAdjustmentForm(false);
    setAdjustmentData({ appointment_id: '', tip: '', service_amount: '', note: '' });
    setAdjustmentError(null);
    setAdjustmentSuccess(null);
    setAppointmentPreview(null);
  };

  const handleRemoveItem = async (itemId) => {
    if (!selectedPayroll) return;
    if (!window.confirm('Remove this item from the payroll?')) return;
    try {
      const updated = await apiService.removePayrollItem(selectedPayroll.id, itemId);
      setSelectedPayroll(updated);
      fetchData(false);
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Failed to remove item');
    }
  };

  // Week navigation
  const navigateWeek = (direction) => {
    const currentWeekNum = parseInt(weekFilter) || prevWeekInfo.week;
    const currentYearNum = parseInt(yearFilter) || prevWeekInfo.year;

    let newWeek, newYear;

    if (direction === 'prev') {
      if (currentWeekNum === 1) {
        newWeek = 52;
        newYear = currentYearNum - 1;
      } else {
        newWeek = currentWeekNum - 1;
        newYear = currentYearNum;
      }
    } else {
      if (currentWeekNum === 52) {
        newWeek = 1;
        newYear = currentYearNum + 1;
      } else {
        newWeek = currentWeekNum + 1;
        newYear = currentYearNum;
      }
    }

    setWeekFilter(newWeek.toString());
    setYearFilter(newYear.toString());
    setCurrentPage(1);
  };

  // Check if payroll is overdue (pending but from a past week)
  const isPayrollOverdue = (payroll) => {
    if (payroll.confirmed) return false;
    const now = new Date();
    const currentWeek = getISOWeek(now);
    const currentYear = now.getFullYear();

    // If from a previous year, it's overdue
    if (payroll.year < currentYear) return true;
    // If same year but week is more than 2 weeks ago, it's overdue
    if (payroll.year === currentYear && payroll.week < currentWeek - 1) return true;
    return false;
  };

  const getPayrollStatus = (payroll) => {
    if (payroll.confirmed) return 'paid';
    if (payroll.mercury_request_id) return 'processing_mercury';
    return 'pending';
  };

  const getStatusColor = (status, isOverdue = false) => {
    if (status === 'paid') return 'bg-green-100 text-green-700';
    if (status === 'processing_mercury') return 'bg-blue-100 text-blue-700';
    if (isOverdue) return 'bg-red-100 text-red-700';
    return 'bg-yellow-100 text-yellow-700';
  };

  const getStatusLabel = (payroll) => {
    if (payroll.confirmed) return 'paid';
    if (payroll.mercury_request_id) return 'processing_mercury';
    if (isPayrollOverdue(payroll)) return 'overdue';
    return 'pending';
  };

  const formatCurrency = (amount) => {
    if (amount === 0) return '$0.00';
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Calculate totals
  const totalAmount = filteredAndSortedData.reduce((sum, payroll) => sum + (parseFloat(payroll.amount) || 0), 0);
  const paidAmount = filteredAndSortedData.filter(p => p.confirmed).reduce((sum, payroll) => sum + (parseFloat(payroll.amount) || 0), 0);
  const pendingAmount = filteredAndSortedData.filter(p => !p.confirmed).reduce((sum, payroll) => sum + (parseFloat(payroll.amount) || 0), 0);

  // Get current week number
  const getCurrentWeek = () => {
    return getISOWeek(new Date());
  };

  return (
    <div className="flex-1 bg-neutral-50 p-3 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      {/* Page Title */}
      <div className="mb-3">
        <h1 className="text-lg font-semibold text-gray-900 mb-2">TEAM PAYOUT</h1>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="h-64">
          <LoadingSpinner />
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-2 mb-3">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading payrolls</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Create Payroll Section */}
          <div className="bg-white border border-gray-300 rounded-lg p-3 mb-3">
            <h2 className="text-sm font-semibold text-gray-900 mb-2">Create Payout</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
              <select
                value={createPayroll.technician}
                onChange={(e) => setCreatePayroll({...createPayroll, technician: e.target.value})}
                className="w-full p-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-800"
              >
                <option value="">Select Team Member</option>
                {technicians.filter(t => t.internal_status === 'active').map((tech) => (
                  <option key={tech.id} value={tech.id}>
                    {tech.person?.first_name} {tech.person?.last_name}
                  </option>
                ))}
              </select>

              <select
                value={createPayroll.week}
                onChange={(e) => setCreatePayroll({...createPayroll, week: e.target.value})}
                className="w-full p-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-800"
              >
                <option value="">Select Week</option>
                {Array.from({length: 52}, (_, i) => i + 1).map(week => (
                  <option key={week} value={week}>
                    Week {week} {week === getCurrentWeek() ? '(current)' : ''}
                  </option>
                ))}
              </select>

              <select
                value={createPayroll.year}
                onChange={(e) => setCreatePayroll({...createPayroll, year: e.target.value})}
                className="w-full p-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-800"
              >
                <option value="2026">2026</option>
                <option value="2025">2025</option>
                <option value="2024">2024</option>
              </select>

              <button
                onClick={handleCreatePayroll}
                disabled={creating || creatingAll}
                className="bg-red-800 text-white px-3 py-2 rounded-lg hover:bg-red-900 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {creating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Create
                  </>
                )}
              </button>

              <button
                onClick={handleCreateAllPayrolls}
                disabled={creating || creatingAll}
                className="bg-green-700 text-white px-3 py-2 rounded-lg hover:bg-green-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                title="Create payouts for all active team members"
              >
                {creatingAll ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Creating All...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Create All
                  </>
                )}
              </button>
            </div>
            {createError && (
              <p className="text-red-600 text-xs mt-2">{createError}</p>
            )}
            {createAllResult && (
              <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs">
                <p className="text-green-800 font-medium">
                  Created {createAllResult.created?.length || 0} payrolls for Week {createAllResult.week}, {createAllResult.year}
                </p>
                {createAllResult.skipped?.length > 0 && (
                  <p className="text-gray-600 mt-1">
                    Skipped {createAllResult.skipped.length} (already exist): {createAllResult.skipped.join(', ')}
                  </p>
                )}
                {createAllResult.created?.length > 0 && (
                  <p className="text-green-700 mt-1">
                    Total: ${createAllResult.created.reduce((sum, p) => sum + p.amount, 0).toFixed(2)}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
            <div className="bg-white rounded-lg p-2 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Total Payout</p>
                  <p className="text-sm font-semibold text-gray-900">{formatCurrency(totalAmount)}</p>
                </div>
                <DollarSign className="w-6 h-6 text-red-800" />
              </div>
            </div>
            <div className="bg-white rounded-lg p-2 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Paid</p>
                  <p className="text-sm font-semibold text-green-600">{formatCurrency(paidAmount)}</p>
                </div>
                <Check className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="bg-white rounded-lg p-2 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Pending</p>
                  <p className="text-sm font-semibold text-yellow-600">{formatCurrency(pendingAmount)}</p>
                </div>
                <AlertCircle className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
            <div className="bg-white rounded-lg p-2 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Records</p>
                  <p className="text-sm font-semibold text-gray-900">{filteredAndSortedData.length}</p>
                </div>
                <User className="w-6 h-6 text-red-800" />
              </div>
            </div>
          </div>

          {/* Week Navigation */}
          <div className="bg-white rounded-lg shadow-sm p-3 mb-3">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigateWeek('prev')}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Previous Week"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-600" />
                </button>
                <div className="text-center min-w-[140px]">
                  <span className="text-sm font-semibold text-gray-900">
                    Week {weekFilter !== 'all' ? weekFilter : '-'}, {yearFilter !== 'all' ? yearFilter : '-'}
                  </span>
                </div>
                <button
                  onClick={() => navigateWeek('next')}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Next Week"
                >
                  <ChevronRight className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              <button
                onClick={() => {
                  setWeekFilter('all');
                  setYearFilter('all');
                }}
                className="text-xs text-red-800 hover:text-red-900"
              >
                Show All
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                <input
                  type="text"
                  placeholder="Search team member..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-1 text-xs bg-neutral-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800 focus:border-transparent"
                />
              </div>

              <select
                value={technicianFilter}
                onChange={(e) => setTechnicianFilter(e.target.value)}
                className="w-full px-2 py-1 text-xs bg-neutral-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
              >
                <option value="all">All Team Members</option>
                {technicianNames.map((tech, index) => (
                  <option key={index} value={tech}>{tech}</option>
                ))}
              </select>

              <select
                value={weekFilter}
                onChange={(e) => { setWeekFilter(e.target.value); setCurrentPage(1); }}
                className="w-full px-2 py-1 text-xs bg-neutral-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
              >
                <option value="all">All Weeks</option>
                {Array.from({length: 52}, (_, i) => i + 1).map(week => (
                  <option key={week} value={week.toString()}>Week {week}</option>
                ))}
              </select>

              <select
                value={yearFilter}
                onChange={(e) => { setYearFilter(e.target.value); setCurrentPage(1); }}
                className="w-full px-2 py-1 text-xs bg-neutral-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
              >
                <option value="all">All Years</option>
                <option value="2026">2026</option>
                <option value="2025">2025</option>
                <option value="2024">2024</option>
              </select>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-2 py-1 text-xs bg-neutral-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
              >
                <option value="all">All Status</option>
                <option value="paid">Paid</option>
                <option value="processing_mercury">Processing (Mercury)</option>
                <option value="pending">Pending</option>
              </select>
            </div>
          </div>

          {/* Payment Result Notification */}
          {paymentResult && (
            <div className={`mb-3 p-3 rounded-lg flex items-center justify-between ${
              paymentResult.success ? 'bg-blue-50 border border-blue-200' : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-center gap-2">
                {paymentResult.success ? (
                  <Send className="w-4 h-4 text-blue-600" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-red-600" />
                )}
                <span className={`text-sm ${paymentResult.success ? 'text-blue-800' : 'text-red-800'}`}>
                  {paymentResult.message}
                </span>
              </div>
              <button
                onClick={() => setPaymentResult(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Email Result Notification */}
          {emailResult && (
            <div className={`mb-3 p-3 rounded-lg flex items-center justify-between ${
              emailResult.success ? 'bg-purple-50 border border-purple-200' : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-center gap-2">
                {emailResult.success ? (
                  <Mail className="w-4 h-4 text-purple-600" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-red-600" />
                )}
                <span className={`text-sm ${emailResult.success ? 'text-purple-800' : 'text-red-800'}`}>
                  {emailResult.message}
                </span>
              </div>
              <button
                onClick={() => setEmailResult(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Payrolls Table */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">TEAM PAYOUTS ({filteredAndSortedData.length})</h3>
              <div className="flex space-x-2">
                <button className="flex items-center space-x-1 px-3 py-1 text-xs text-gray-600 hover:text-gray-800">
                  <Download className="w-3.5 h-3.5" />
                  <span>Export</span>
                </button>
                <button className="flex items-center space-x-1 px-3 py-1 text-xs text-gray-600 hover:text-gray-800">
                  <Printer className="w-3.5 h-3.5" />
                  <span>Print</span>
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('technician')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Team Member</span>
                        {sortField === 'technician' && (
                          sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('week')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Week</span>
                        {sortField === 'week' && (
                          sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('year')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Year</span>
                        {sortField === 'year' && (
                          sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                        )}
                      </div>
                    </th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Appts
                    </th>
                    <th
                      className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('amount')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Amount</span>
                        {sortField === 'amount' && (
                          sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                        )}
                      </div>
                    </th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentData.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-8 text-center text-gray-500 text-sm">
                        No payrolls found. Create one above.
                      </td>
                    </tr>
                  ) : (
                    currentData.map((payroll) => {
                      const statusLabel = getStatusLabel(payroll);
                      const overdue = isPayrollOverdue(payroll);
                      return (
                        <tr key={payroll.id} className="hover:bg-neutral-50">
                          <td className="px-2 py-1.5 text-xs text-gray-900">{getTechnicianName(payroll)}</td>
                          <td className="px-2 py-1.5 text-xs text-gray-900">{payroll.week}</td>
                          <td className="px-2 py-1.5 text-xs text-gray-900">{payroll.year}</td>
                          <td className="px-2 py-1.5 text-xs text-gray-500">{payroll.appointments_nb || 0}</td>
                          <td className="px-2 py-1.5 text-xs text-gray-900 font-medium">{formatCurrency(payroll.amount)}</td>
                          <td className="px-2 py-1.5 text-xs">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(statusLabel, overdue)}`}>
                              {overdue && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                              {statusLabel}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 text-xs">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleViewDetails(payroll)}
                                className="text-gray-400 hover:text-red-800 transition-colors p-1"
                                title="View Details"
                              >
                                <Eye className="w-3.5 h-3.5" />
                              </button>
                              {!payroll.confirmed && (
                                <>
                                  <button
                                    onClick={() => handleRequestPayment(payroll)}
                                    disabled={requestingPayment === payroll.id}
                                    className="text-gray-400 hover:text-blue-600 transition-colors p-1"
                                    title="Request Mercury Payment"
                                  >
                                    {requestingPayment === payroll.id ? (
                                      <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-blue-600"></div>
                                    ) : (
                                      <Send className="w-3.5 h-3.5" />
                                    )}
                                  </button>
                                </>
                              )}
                              {!payroll.confirmed && (
                                <button
                                  onClick={() => handleRecompute(payroll.id)}
                                  disabled={recomputing === payroll.id}
                                  className="text-gray-400 hover:text-blue-600 transition-colors p-1"
                                  title="Recompute with current data"
                                >
                                  {recomputing === payroll.id ? (
                                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-blue-600"></div>
                                  ) : (
                                    <RefreshCw className="w-3.5 h-3.5" />
                                  )}
                                </button>
                              )}
                              <button
                                onClick={() => handleSendEmail(payroll)}
                                disabled={sendingEmail === payroll.id}
                                className="text-gray-400 hover:text-purple-600 transition-colors p-1"
                                title="Send Payout Email"
                              >
                                {sendingEmail === payroll.id ? (
                                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-purple-600"></div>
                                ) : (
                                  <Mail className="w-3.5 h-3.5" />
                                )}
                              </button>
                              <button
                                onClick={() => handleDeletePayroll(payroll.id)}
                                disabled={deleting === payroll.id}
                                className="text-gray-400 hover:text-red-600 transition-colors p-1"
                                title="Delete"
                              >
                                {deleting === payroll.id ? (
                                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-red-600"></div>
                                ) : (
                                  <Trash2 className="w-3.5 h-3.5" />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-2 py-2 border-t border-gray-200 flex items-center justify-between">
                <div className="text-xs text-gray-700">
                  Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(endIndex, filteredAndSortedData.length)}</span> of{' '}
                  <span className="font-medium">{filteredAndSortedData.length}</span>
                </div>
                <div className="flex gap-1">
                  <Button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-2 py-1 text-xs"
                  >
                    Previous
                  </Button>
                  <Button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-2 py-1 text-xs"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Payroll Detail Modal */}
      {showDetailModal && selectedPayroll && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowDetailModal(false)}>
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-red-800 text-white px-4 py-3 flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                Payroll Details - Week {selectedPayroll.week}, {selectedPayroll.year}
              </h2>
              <button onClick={() => setShowDetailModal(false)} className="text-white hover:text-gray-200">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[70vh]">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Team Member</p>
                  <p className="text-sm font-semibold">{getTechnicianName(selectedPayroll)}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Total Amount</p>
                  <p className="text-sm font-semibold text-green-600">{formatCurrency(selectedPayroll.amount)}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Appointments</p>
                  <p className="text-sm font-semibold">{selectedPayroll.appointments_nb || 0}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Status</p>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(getPayrollStatus(selectedPayroll))}`}>
                    {getPayrollStatus(selectedPayroll)}
                  </span>
                </div>
              </div>

              {/* Items List */}
              <h3 className="text-sm font-semibold mb-2">Items</h3>
              {selectedPayroll.items && selectedPayroll.items.length > 0 ? (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Client</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Service</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Tip</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Total</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {selectedPayroll.items.map((item, idx) => {
                        const isAdjustment = !item.appointment;
                        const isDeduction = isAdjustment && parseFloat(item.total_amount) < 0;
                        return (
                          <React.Fragment key={idx}>
                            <tr className={isDeduction ? 'bg-red-50' : isAdjustment ? 'bg-amber-50' : 'hover:bg-gray-50'}>
                              <td className="px-3 py-2 text-xs text-gray-900">
                                {isAdjustment ? (
                                  <span className="inline-flex items-center gap-1">
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${isDeduction ? 'bg-red-200 text-red-800' : 'bg-amber-200 text-amber-800'}`}>{isDeduction ? 'DED' : 'ADJ'}</span>
                                    {item.note || (isDeduction ? 'Deduction' : 'Adjustment')}
                                  </span>
                                ) : (
                                  item.appointment?.client_name || 'N/A'
                                )}
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-500">
                                {item.appointment?.date_time
                                  ? new Date(item.appointment.date_time).toLocaleDateString()
                                  : '—'}
                              </td>
                              <td className={`px-3 py-2 text-xs text-right ${parseFloat(item.service_amount) < 0 ? 'text-red-600' : ''}`}>{formatCurrency(item.service_amount)}</td>
                              <td className={`px-3 py-2 text-xs text-right ${parseFloat(item.tip) < 0 ? 'text-red-600' : 'text-green-600'}`}>{formatCurrency(item.tip)}</td>
                              <td className={`px-3 py-2 text-xs text-right font-medium ${parseFloat(item.total_amount) < 0 ? 'text-red-600' : ''}`}>{formatCurrency(item.total_amount)}</td>
                              <td className="px-3 py-2 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  {item.appointment?.id && (
                                    <a
                                      href={`/appointments/${item.appointment.id}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center justify-center p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                                      title="Open appointment"
                                    >
                                      <ExternalLink className="w-3.5 h-3.5" />
                                    </a>
                                  )}
                                  {!selectedPayroll.confirmed && (
                                    <button
                                      onClick={() => handleRemoveItem(item.id)}
                                      className="inline-flex items-center justify-center p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                                      title="Remove item"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                            {item.note && item.appointment && (
                              <tr className={isAdjustment ? 'bg-amber-50' : ''}>
                                <td colSpan="6" className="px-3 pb-2 pt-0 text-xs italic text-gray-500">
                                  Note: {item.note}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td colSpan="4" className="px-3 py-2 text-xs font-semibold text-right">Total:</td>
                        <td className="px-3 py-2 text-xs font-semibold text-right">{formatCurrency(selectedPayroll.amount)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No items in this payroll</p>
              )}

              {/* Add Adjustment Section - only for unconfirmed payrolls */}
              {!selectedPayroll.confirmed && (
                <div className="mt-4">
                  {adjustmentSuccess && (
                    <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700 flex items-center gap-1">
                      <Check className="w-3 h-3" /> {adjustmentSuccess}
                    </div>
                  )}

                  {!showAdjustmentForm ? (
                    <button
                      onClick={() => { setShowAdjustmentForm(true); setAdjustmentSuccess(null); }}
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      <Plus className="w-3 h-3" /> Add Adjustment (late tip, correction)
                    </button>
                  ) : (
                    <div className="border border-blue-200 rounded-lg p-3 bg-blue-50">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs font-semibold text-blue-800">Add Adjustment</h4>
                        <button onClick={resetAdjustmentForm} className="text-gray-400 hover:text-gray-600">
                          <X className="w-4 h-4" />
                        </button>
                      </div>

                      {adjustmentError && (
                        <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> {adjustmentError}
                        </div>
                      )}

                      {/* Appointment ID with lookup */}
                      <div className="mb-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Appointment ID <span className="font-normal text-gray-400">(optional)</span></label>
                        <div className="flex gap-1">
                          <input
                            type="text"
                            value={adjustmentData.appointment_id}
                            onChange={e => setAdjustmentData({...adjustmentData, appointment_id: e.target.value})}
                            className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs"
                            placeholder="e.g. 5226"
                          />
                          <button
                            onClick={handleLookupAppointment}
                            disabled={!adjustmentData.appointment_id.trim() || lookingUpAppointment}
                            className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300 disabled:opacity-50"
                          >
                            {lookingUpAppointment ? '...' : 'Lookup'}
                          </button>
                        </div>
                      </div>

                      {/* Appointment preview */}
                      {appointmentPreview && (
                        <div className="mb-2 p-2 bg-white border border-gray-200 rounded text-xs">
                          <p><span className="font-medium">Client:</span> {appointmentPreview.client_name || 'N/A'}</p>
                          <p><span className="font-medium">Date:</span> {appointmentPreview.date_time ? new Date(appointmentPreview.date_time).toLocaleDateString() : 'N/A'}</p>
                          <p><span className="font-medium">Service:</span> {appointmentPreview.service_name || appointmentPreview.services?.map(s => s.name).join(', ') || 'N/A'}</p>
                          <p><span className="font-medium">Status:</span> {appointmentPreview.status}</p>
                        </div>
                      )}

                      {/* Tip and Service Amount */}
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Tip ($)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={adjustmentData.tip}
                            onChange={e => setAdjustmentData({...adjustmentData, tip: e.target.value})}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                            placeholder="0.00 (use negative for deductions)"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Service ($)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={adjustmentData.service_amount}
                            onChange={e => setAdjustmentData({...adjustmentData, service_amount: e.target.value})}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                            placeholder="0.00 (use negative for deductions)"
                          />
                        </div>
                      </div>

                      {/* Note */}
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Note</label>
                        <input
                          type="text"
                          value={adjustmentData.note}
                          onChange={e => setAdjustmentData({...adjustmentData, note: e.target.value})}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                          placeholder="e.g. Late tip from Rose Kohn 2/9"
                        />
                      </div>

                      {/* Submit */}
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={resetAdjustmentForm}
                          className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleAddAdjustment}
                          disabled={adjustmentLoading || (!adjustmentData.tip && !adjustmentData.service_amount)}
                          className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50"
                        >
                          {adjustmentLoading ? 'Adding...' : 'Add Adjustment / Deduction'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => { setShowDetailModal(false); resetAdjustmentForm(); }}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Preview Modal */}
      {showEmailPreview && emailPreviewData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-red-800 text-white px-4 py-3 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Email Preview</h2>
              <button onClick={() => setShowEmailPreview(false)} className="text-white hover:text-gray-200">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Email metadata */}
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 text-sm">
              <div className="grid grid-cols-[80px_1fr] gap-1">
                <span className="font-medium text-gray-600">To:</span>
                <span>{emailPreviewData.recipient}</span>
                <span className="font-medium text-gray-600">CC:</span>
                <span>{emailPreviewData.cc}</span>
                <span className="font-medium text-gray-600">Subject:</span>
                <span className="font-medium">{emailPreviewData.subject}</span>
              </div>
            </div>

            {/* Email content preview */}
            <div className="flex-1 overflow-auto p-4">
              <div
                className="border border-gray-200 rounded-lg overflow-hidden"
                dangerouslySetInnerHTML={{ __html: emailPreviewData.html_content }}
              />
            </div>

            {/* Actions */}
            <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setShowEmailPreview(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSendEmail}
                disabled={sendingEmail}
                className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 text-sm flex items-center gap-2"
              >
                {sendingEmail ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Mail className="w-4 h-4" />
                )}
                Send Email
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PayrollPage;
